import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Box, Button, TextField, Tooltip, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useDeleteCraft, useUpsertCraft } from "../hooks/useCraftsMutations";
import { useCrafts } from "../hooks/useCraftsQueries";
import {
	CRAFT_INITIAL_VALUES,
	type CraftRow,
	type ModalMode,
	firstFreeColor,
} from "../models/Craft";
import { CraftColorPicker } from "./CraftColorPicker";
import { CraftLetterBadge } from "./CraftLetterBadge";

type Props = {
	modal: ModalMode;
	craft: CraftRow | null;
	onHide: (savedMessage?: string) => void;
};

export const UpsertDeleteCraft = ({ modal, craft, onHide }: Props) => {
	const [error, setError] = useState<string>("");
	const { crafts } = useCrafts();

	const { control, reset, handleSubmit, watch } = useForm<CraftRow>({
		defaultValues: CRAFT_INITIAL_VALUES,
	});

	// Only when the modal opens: a refetch of the list must not reset what is being typed
	// biome-ignore lint/correctness/useExhaustiveDependencies: crafts is read once on open
	useEffect(() => {
		if (modal === "") return;
		reset(
			modal === "new" || !craft
				? { ...CRAFT_INITIAL_VALUES, Color: firstFreeColor(crafts) }
				: { ...craft, Code: craft.Code ?? "" },
		);
	}, [modal, craft, reset]);

	const { upsertCraft, isSaving } = useUpsertCraft({
		onSuccess: () =>
			onHide(modal === "new" ? "The craft was added" : "The craft was updated"),
		onError: (e) => setError(e.message),
	});

	const { deleteCraft, isDeleting } = useDeleteCraft({
		onSuccess: () => onHide("The craft was deleted"),
		onError: (e) => setError(e.message),
	});

	const onSubmit: SubmitHandler<CraftRow> = (data) => {
		if (modal === "delete") {
			deleteCraft(craft?.CraftId as number);
			return;
		}
		upsertCraft({
			...data,
			CraftId: modal === "new" ? null : (craft?.CraftId ?? null),
		});
	};

	const workOrderCount = craft?.WorkOrderCount ?? 0;
	const templateCount = craft?.TemplateCount ?? 0;
	const isInUse = workOrderCount > 0 || templateCount > 0;
	const title =
		modal === "new" ? "CREATE" : modal === "edit" ? "EDIT" : "DELETE";
	const [previewName, previewCode, previewColor] = watch([
		"CraftName",
		"Code",
		"Color",
	]);

	return (
		<>
			<MesfModal
				title={`${title} CRAFT`}
				open={modal !== ""}
				handleClose={() => onHide()}
				maxWidth="sm"
				id="craft-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						{modal === "delete" ? (
							<Grid2 container spacing={1}>
								<Grid2 size={12}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography>Delete</Typography>
										<CraftLetterBadge
											name={craft?.CraftName}
											code={craft?.Code}
											color={craft?.Color}
										/>
										<Typography>{craft?.CraftName}?</Typography>
									</Box>
								</Grid2>
								{isInUse && (
									<Grid2 size={12}>
										<Typography color="error" sx={{ mt: 1 }}>
											This craft cannot be deleted. It is used by{" "}
											{workOrderCount} work order(s) and {templateCount}{" "}
											template(s), including closed orders and deleted
											templates.
										</Typography>
									</Grid2>
								)}
							</Grid2>
						) : (
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, sm: 8 }}>
									<Controller
										name="CraftName"
										control={control}
										rules={{
											validate: (v) =>
												(v ?? "").trim() !== "" || "Name is required",
										}}
										render={({
											field: { onChange, value },
											fieldState: { error },
										}) => (
											<TextField
												label="Name"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												onChange={onChange}
												value={value ?? ""}
												error={!!error}
												helperText={error?.message}
												slotProps={{ htmlInput: { maxLength: 20 } }}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, sm: 4 }}>
									<Controller
										name="Code"
										control={control}
										rules={{
											pattern: {
												value: /^[A-Za-z0-9]?$/,
												message: "One letter or digit",
											},
										}}
										render={({
											field: { onChange, value },
											fieldState: { error },
										}) => (
											<Tooltip
												title="Optional. Leave it empty to use the first letter of the name."
												placement="top"
												arrow
											>
												<TextField
													label="Badge Letter"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													placeholder={(previewName ?? "")
														.trim()
														.charAt(0)
														.toUpperCase()}
													onChange={(e) =>
														onChange(e.target.value.toUpperCase())
													}
													value={value ?? ""}
													error={!!error}
													helperText={error?.message}
													slotProps={{
														htmlInput: { maxLength: 1 },
														inputLabel: { shrink: true },
													}}
												/>
											</Tooltip>
										)}
									/>
								</Grid2>
								<Grid2 size={12} sx={{ mt: 1 }}>
									<Controller
										name="Color"
										control={control}
										render={({ field: { onChange, value } }) => (
											<CraftColorPicker
												value={value}
												craftId={
													modal === "new" ? null : (craft?.CraftId ?? null)
												}
												onChange={onChange}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={12} sx={{ mt: 2 }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Typography
											variant="body2"
											sx={{ color: "text.secondary" }}
										>
											Preview
										</Typography>
										<CraftLetterBadge
											name={previewName}
											code={previewCode}
											color={previewColor}
										/>
										<Typography variant="body2">{previewName}</Typography>
									</Box>
								</Grid2>
							</Grid2>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide()}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSaving || isDeleting}
									disabled={modal === "delete" && isInUse}
									variant="contained"
									color={modal === "delete" ? "secondary" : "primary"}
									type="submit"
								>
									{modal === "delete" ? "Delete" : "Save"}
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
